import { readJsonString } from '@dealstack/shared/lib/format/json';
import {
	AuditEntry,
	TransformedAuditData,
} from '@dealstack/frontend/shared/components/Audit/types.ts';
import { AuditAction, AuditTct } from '@dealstack/shared/lib/generated';

const BANK_ACCOUNT_TABLE = 'BankAccount';
const BANK_ACCOUNT_IDENTIFIER_TABLE = 'BankAccountIdentifier';

/** The account a row belongs to — its own id, or the one its identifier row names. */
const readBankAccountId = (audit: AuditTct): string | undefined => {
	if (audit.tableName === BANK_ACCOUNT_TABLE) {
		return audit.recordId;
	}
	if (audit.tableName !== BANK_ACCOUNT_IDENTIFIER_TABLE) {
		return undefined;
	}

	return (
		readJsonString(audit.newData, 'bankAccountId') ??
		readJsonString(audit.oldData, 'bankAccountId')
	);
};

/**
 * The write's net effect on one row. A save hits the account more than once — the field
 * update, the validation-method stamp and the status transition are separate statements,
 * each with its own audit row — and read as-is that repeats every field once per
 * statement, and hides a create behind the stamp that follows it.
 */
export const toNetAudit = (recordAudits: AuditTct[]): AuditTct => {
	const newest = recordAudits[0];
	const oldest = recordAudits.at(-1) ?? newest;

	if (oldest.action === AuditAction.CREATE) {
		return { ...newest, action: AuditAction.CREATE, oldData: undefined };
	}
	if (newest.action === AuditAction.DELETE || newest.action === AuditAction.RESTORE) {
		return { ...newest, oldData: oldest.oldData };
	}

	return {
		...newest,
		action: AuditAction.UPDATE,
		oldData: oldest.oldData,
		newData: newest.newData,
	};
};

/** One audit per row the write touched, newest row first. */
export const toNetAudits = (group: AuditTct[]): AuditTct[] => {
	const byRecord = new Map<string, AuditTct[]>();

	group.forEach((audit) => {
		const key = `${audit.tableName}:${audit.recordId}`;
		byRecord.set(key, [...(byRecord.get(key) ?? []), audit]);
	});

	return [...byRecord.values()].map(toNetAudit);
};

/**
 * A create or delete records one value per field; rendered next to an update's own
 * before/after columns it needs a side to sit on.
 */
export const toBeforeAfter = (row: TransformedAuditData): TransformedAuditData => {
	if (row.type === 'created') {
		return { ...row, beforeValue: '', afterValue: row.value, isChanged: true, type: 'update' };
	}
	if (row.type === 'deleted') {
		return { ...row, beforeValue: row.value, afterValue: '', isChanged: true, type: 'update' };
	}

	return row;
};

// The account row carries what a reviewer reads first; its identifiers follow.
const bankAccountFirst = (a: AuditTct, b: AuditTct): number =>
	Number(a.tableName !== BANK_ACCOUNT_TABLE) - Number(b.tableName !== BANK_ACCOUNT_TABLE);

interface Write {
	/** Set when the write is a bank account's, which is what gets collapsed. */
	bankAccountId?: string;
	audits: AuditTct[];
}

const toEntry = ({ bankAccountId, audits: written }: Write): AuditEntry => {
	if (!bankAccountId) {
		return { audit: written[0], audits: written };
	}

	const audits = toNetAudits(written).sort(bankAccountFirst);
	const [newest] = audits;
	// An account created alongside its identifiers stays a create; a write that mixes
	// actions is read as an update, so every row has a side to sit on.
	const isMixed = audits.some((audit) => audit.action !== newest.action);

	return {
		// Always the account's entry, even for a write that only moved its identifiers: a
		// detail row on its own is not something a reviewer looks up by name.
		audit: {
			...newest,
			tableName: BANK_ACCOUNT_TABLE,
			recordId: bankAccountId,
			action: isMixed ? AuditAction.UPDATE : newest.action,
		},
		audits,
	};
};

/**
 * One entry per write for a bank account, one per audit for every other table. A save
 * touches the account row and each identifier it changed, and the trigger dates them all
 * with NOW(), which holds still for a transaction — so the same account id and createdAt
 * mark one action.
 */
export const toAuditEntries = (audits: AuditTct[]): AuditEntry[] => {
	const writes: Write[] = [];
	const byWrite = new Map<string, Write>();

	audits.forEach((audit) => {
		const bankAccountId = readBankAccountId(audit);

		if (!bankAccountId) {
			writes.push({ audits: [audit] });

			return;
		}

		const key = `${audit.createdAt}:${bankAccountId}`;
		const started = byWrite.get(key);

		if (started) {
			started.audits.push(audit);

			return;
		}

		const write: Write = { bankAccountId, audits: [audit] };
		byWrite.set(key, write);
		writes.push(write);
	});

	return writes.map(toEntry);
};

/**
 * What one entry changed: the rows of every audit it collapsed, read out as one table.
 * `action` overrides the columns the rows are shaped for, for a view that renders one set
 * whatever the entry did.
 */
export const toEntryRows = (
	entry: AuditEntry,
	transform: (audit: AuditTct) => TransformedAuditData[],
	action: AuditAction = entry.audit.action
): TransformedAuditData[] =>
	entry.audits.flatMap((audit) =>
		action === AuditAction.UPDATE ? transform(audit).map(toBeforeAfter) : transform(audit)
	);
